/**
 * @module VerifyStrategyHelpers
 * @description Phase 14 consolidation of shared non-VoIP verify helpers.
 * Keeps Scripted, Web, WiFi, and BTT strategy prerequisites and result shaping aligned in one module.
 */

import { t } from '@lingui/macro';
import { forEach, get, omit } from 'lodash';
import { AGENT_RESULT_STATUS, AGENT_STATUS_RUNNING, MAX_VERIFY_AGENTS } from './VerifySharedConstants';
import {
    SCRIPTED_ALLOW_NEGATIVE,
    SCRIPTED_METRICS_OMIT_FIELDS,
    translateScriptedErrorCode,
} from './VerifyScriptedConstants';
import { WIRELESS } from '../../ServiceTestsConstants';

const BTT_TEST = 'bttTest';
const BTT_SUPPORTED = 'bttSupported';
const IS_WIRELESS_ENABLED = 'isWirelessEnabled';
const IS_DYNAMIC_WIRELESS_ENABLED = 'isDynamicWirelessEnabled';

/**
 * Filter and limit agents for verify tests (Scripted, Web, WiFi, BTT).
 * portal/ui source: verify.service.js getVerifyAgents()
 *
 * Agents must be Running (status === 'Running') and at most MAX_VERIFY_AGENTS (10).
 * BTT, WiFi, and WiFi Connect agents are additionally filtered by capability.
 *
 * @param {Array} agents - Available agents from the agent selector
 * @param {Object} formData - Current form data (needs adapterType)
 * @param {Object} testType - Test type configuration object
 * @returns {string[]} Array of agent IDs
 * @throws {Error} If no eligible agents found
 */
export function getVerifyAgents(agents, formData, testType) {
    const allAgents = agents || [];
    const isBtt = get(testType, BTT_TEST, false);
    const isWifiConnect = !!testType?.wifiConnectTest;
    const isWireless = formData.adapterType === WIRELESS;

    const verifyAgents = [];

    /* eslint-disable no-restricted-syntax, no-continue */
    for (const agent of allAgents) {
        if (verifyAgents.length >= MAX_VERIFY_AGENTS) {
            break;
        }

        if (agent.status !== AGENT_STATUS_RUNNING) {
            continue;
        }
        if (isBtt && !get(agent, BTT_SUPPORTED, false)) {
            continue;
        }
        if (isWireless && !isWifiConnect && !get(agent, IS_WIRELESS_ENABLED, false)) {
            continue;
        }
        if (isWifiConnect && !get(agent, IS_DYNAMIC_WIRELESS_ENABLED, false)) {
            continue;
        }

        verifyAgents.push(agent._id);
    }
    /* eslint-disable no-restricted-syntax, no-continue */

    return ensureVerifyAgentsFound(verifyAgents, isWireless);
}

function ensureVerifyAgentsFound(verifyAgents, isWireless) {
    if (verifyAgents.length === 0) {
        if (isWireless) {
            throw new Error(
                t`There are no running nPoint 3000h models available to verify this test configuration. Check the nPoint Interface settings and try again.`
            );
        }

        throw new Error(
            t`There are no running nPoints available to verify this test configuration. Check the Run On nPoints setting and try again.`
        );
    }

    return verifyAgents;
}

/**
 * Create an empty agent result object matching portal/ui's QuickTestsService.createAgentResultObject.
 * @param {string} agentId
 * @returns {Object}
 */
export const createAgentResultObject = (agentId) => ({
    agent: agentId,
    resultCount: 0,
    status: AGENT_RESULT_STATUS.INITIALIZING,
});

/**
 * Process a single test result entry into a structured display object.
 * Shared by both Web and Scripted strategies since portal/ui routes Web through
 * VerifyScriptedTestService by default.
 *
 * portal/ui source: verifyScriptedTest.service.js buildOneResult/buildOneSuccess/buildOneFailure
 *
 * @param {Object} result - Single result from agentResult.results[]
 * @returns {Object} Structured result { type, errorCode?, metrics?, logFile? }
 */
export function processOneResult(result) {
    if (!result) {
        return { type: 'empty', message: t`No results` };
    }

    const availability = get(result, 'data.availability');
    const scriptAvailability = get(result, 'data.scriptAvailability');
    const errorCode = get(result, 'data.errorCode');

    if ((availability === 0 || scriptAvailability === 0) && errorCode) {
        const errorValue = get(result, 'data.errorValue', '');
        const cleanedErrorValue =
            typeof errorValue === 'string' && errorValue.charAt(0) === '"' ? errorValue.replace(/"/g, '') : errorValue;
        const errorTrace = get(result, 'data.errorTrace', '');
        const logFile = get(result, 'data.log_file', '');

        return {
            type: 'failure',
            errorCode: translateScriptedErrorCode(errorCode),
            errorValue: cleanedErrorValue,
            errorTrace,
            logFile,
        };
    }

    const metrics = [];
    const data = omit(result.data || {}, SCRIPTED_METRICS_OMIT_FIELDS);
    forEach(data, (value, key) => {
        let displayValue = value;
        if (!Number.isNaN(Number(value)) && !SCRIPTED_ALLOW_NEGATIVE.includes(key) && value < 0) {
            displayValue = '--';
        }
        metrics.push({ key, value: displayValue });
    });

    return {
        type: 'success',
        metrics,
        logFile: get(result, 'data.log_file', ''),
    };
}
